import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { CTA } from '../components/CTA';
import { 
  UserCog, 
  CheckCircle2, 
  Phone, 
  ClipboardCheck, 
  Shield, 
  Calendar,
  MessageSquare,
  FileText,
  Users,
  Award
} from 'lucide-react';

const benefits = [
  {
    icon: Phone,
    title: 'Jederzeit erreichbar',
    description: 'Bei Fragen zum QM-System, zu Dokumenten oder Prozessen bin ich Ihr direkter Ansprechpartner – per Telefon oder E-Mail.'
  },
  {
    icon: ClipboardCheck,
    title: 'Interne Audits inklusive',
    description: 'Das jährlich vorgeschriebene interne Audit führe ich für Sie durch. Sie erhalten einen vollständigen Auditbericht mit allen Feststellungen.'
  },
  {
    icon: Shield,
    title: 'Begleitung im externen Audit',
    description: 'Der größte Vorteil: Bei Überwachungs- und Zertifizierungsaudits bin ich an Ihrer Seite. Als vertrauter QM-Beauftragter kenne ich Ihr System und kann Auditorfragen direkt beantworten.'
  },
  {
    icon: FileText,
    title: 'Dokumentationspflege',
    description: 'Ich halte Ihr QM-System aktuell: Anpassungen bei Änderungen, Pflege der Dokumente und Vorbereitung auf Audits.'
  },
  {
    icon: Users,
    title: 'Ansprechpartner für FKS und AMDL',
    description: 'Kommunikation mit der fachkundigen Stelle oder dem Anbieter des Maßnahmendatenlogos übernehme ich für Sie.'
  },
  {
    icon: Calendar,
    title: 'Planbare Kosten',
    description: 'Monatlicher Festbetrag statt unkalkulierbarer Einzelrechnungen. Sie wissen genau, was die QM-Betreuung kostet.'
  }
];

const includedServices = [
  'Laufende Betreuung des Qualitätsmanagements',
  'Jährliches internes Audit mit Bericht',
  'Begleitung bei externen Audits (FKS)',
  'Pflege und Aktualisierung der QM-Dokumentation',
  'Ansprechpartner für alle QM-Fragen',
  'Kommunikation mit Zertifizierungsstelle',
  'Unterstützung bei Maßnahmezulassungen',
  'Vorbereitung auf Überwachungsaudits'
];

const ExternerQMBeauftragter = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="externer-qm-page">
      <Navbar />
      {/* Hero glow that extends behind navbar */}
      <div className="hero-glow" />
      <main className="pt-20 relative">
        {/* Header - Lebendiges Layout */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="text-primary text-sm font-semibold uppercase tracking-widest mb-6 block">
                  Monatlicher Festbetrag
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.1] mb-8" data-testid="externer-qm-headline">
                  Externer{' '}
                  <span className="text-primary">QM-Beauftragter</span>{' '}
                  für Ihren Träger
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
                  Professionelle QM-Betreuung, interne Audits und Begleitung bei externen Prüfungen – alles für einen planbaren monatlichen Festbetrag.
                </p>
                <Link to="/kontakt">
                  <Button 
                    className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-8 py-6 text-lg transition-all duration-300"
                  >
                    Unverbindlich anfragen
                  </Button>
                </Link>
              </motion.div>

              {/* Right - Leistungen Preview */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="hidden lg:block"
              >
                <div className="bg-card/20 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
                  <h3 className="text-lg font-semibold text-white mb-6">Was Sie erhalten:</h3>
                  <div className="flex flex-col gap-4">
                    {[
                      'Jährliches internes Audit',
                      'Begleitung bei FKS-Audits',
                      'QM-Dokumentation aktuell halten',
                      'Ansprechpartner für QM-Fragen'
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-4 bg-white/[0.02] rounded-xl border border-white/5">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-white">{item}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-center text-muted-foreground text-sm mt-6 italic">
                    + weitere Leistungen nach Bedarf
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Main Benefit Highlight - LIGHT SECTION */}
        <section className="py-12 md:py-16 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-primary/20 rounded-2xl p-8 md:p-12 shadow-sm"
            >
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-16 h-16 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                  <Award className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                    Der größte Vorteil: Begleitung im externen Audit
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Als Ihr externer QM-Beauftragter kenne ich Ihr Qualitätsmanagementsystem im Detail. 
                    Wenn der Auditor der Zertifizierungsstelle kommt, bin ich an Ihrer Seite – nicht als 
                    externer Berater, sondern als Teil Ihres Teams.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    Ich beantworte Fragen zum QM-System, erkläre Prozesse und Dokumente und kann direkt 
                    Auskunft geben. Das schafft Sicherheit im Audit und entlastet Ihr internes Team erheblich.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="py-12 md:py-16 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <span className="text-sm uppercase tracking-widest text-primary font-semibold mb-4 block">
                Leistungen
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight mb-6">
                Was Sie erhalten
              </h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Umfassende QM-Betreuung ohne versteckte Kosten.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-card/50 border border-white/10 rounded-2xl p-8 hover:border-primary/30 transition-all"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                    <benefit.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-white mb-3">{benefit.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included - LIGHT SECTION */}
        <section className="py-12 md:py-16 bg-gray-100">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <span className="text-sm uppercase tracking-widest text-primary font-semibold mb-4 block">
                  Im Festbetrag enthalten
                </span>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight mb-6 text-gray-900">
                  Alle Leistungen auf einen Blick
                </h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Der monatliche Festbetrag deckt alle laufenden QM-Aufgaben ab. 
                  Keine Überraschungen, keine versteckten Kosten.
                </p>
                <ul className="space-y-4">
                  {includedServices.map((service, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-gray-900">{service}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-6">
                    Für wen geeignet?
                  </h3>
                  <div className="flex flex-col gap-6">
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-2">Kleine Bildungsträger</h4>
                      <p className="text-sm text-gray-600">
                        Kein eigener QM-Beauftragter nötig – ich übernehme die Aufgabe extern.
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-2">Überlastete Teams</h4>
                      <p className="text-sm text-gray-600">
                        Wenn das interne QM-System neben dem Tagesgeschäft zu kurz kommt.
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-2">Träger vor dem Audit</h4>
                      <p className="text-sm text-gray-600">
                        Professionelle Begleitung durch einen erfahrenen QM-Experten.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-12 md:py-16 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-16"
            >
              <span className="text-sm uppercase tracking-widest text-primary font-semibold mb-4 block">
                Ablauf
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight">
                So starten wir die Zusammenarbeit
              </h2>
            </motion.div>

            <div className="flex flex-col gap-8">
              {[
                {
                  number: '01',
                  title: 'Erstgespräch',
                  description: 'Wir besprechen Ihre aktuelle Situation, den Umfang Ihres QM-Systems und Ihre Anforderungen.'
                },
                {
                  number: '02',
                  title: 'Angebot',
                  description: 'Sie erhalten ein transparentes Angebot mit einem monatlichen Festbetrag – ohne versteckte Kosten.'
                },
                {
                  number: '03',
                  title: 'Einarbeitung',
                  description: 'Ich mache mich mit Ihrem QM-System vertraut und übernehme die laufende Betreuung.'
                },
                {
                  number: '04',
                  title: 'Laufende Betreuung',
                  description: 'Ab sofort bin ich Ihr Ansprechpartner für alle QM-Fragen, führe Audits durch und begleite Sie bei externen Prüfungen.'
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-6 p-6 bg-card/30 border border-white/10 rounded-2xl"
                >
                  <div className="text-4xl font-heading font-bold text-primary/30">{step.number}</div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA - LIGHT SECTION */}
        <section className="py-12 md:py-16 bg-gray-100 relative overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(7,209,131,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(7,209,131,0.05)_1px,transparent_1px)] bg-[size:64px_64px]" />
          <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight mb-6 text-gray-900">
                QM-Betreuung ohne internen Aufwand?
              </h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto mb-8">
                Lassen Sie uns über Ihre Situation sprechen. Ich erkläre Ihnen, wie die Zusammenarbeit 
                aussehen kann und Sie erhalten ein unverbindliches Angebot.
              </p>
              <Link to="/kontakt">
                <Button 
                  className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-8 py-6 text-lg transition-all duration-300"
                >
                  Kostenlose Ersteinschätzung vereinbaren
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <div className="noise-overlay" />
    </div>
  );
};

export default ExternerQMBeauftragter;
